import React from 'react';
import {
  Modal as RNModal,
  ModalProps as RNModalProps,
  TouchableOpacity,
  Platform,
} from 'react-native';
import styled from 'styled-components/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '@theme/ThemeProvider';
import { Text } from './Text';

interface ModalProps extends RNModalProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

const Overlay = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.overlay};
  justify-content: flex-end;
  ${Platform.OS === 'web'
    ? `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
  `
    : ''}
`;

const ModalContent = styled.View`
  background-color: ${({ theme }) => theme.colors.surface};
  border-top-left-radius: ${({ theme }) => theme.radius.xl}px;
  border-top-right-radius: ${({ theme }) => theme.radius.xl}px;
  padding: ${({ theme }) => theme.spacing.xl}px;
  padding-bottom: ${({ theme }) => theme.spacing.xl + 8}px;
  max-height: 85%;
  width: 100%;
  ${Platform.OS === 'web'
    ? `
    position: relative;
    z-index: 1001;
    max-height: 90vh;
    min-height: 400px;
    display: flex;
    flex-direction: column;
  `
    : ''}
`;

const Header = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const CloseButton = styled(TouchableOpacity)`
  width: ${({ theme }) => theme.minTouchTarget}px;
  height: ${({ theme }) => theme.minTouchTarget}px;
  align-items: center;
  justify-content: center;
  margin-right: -12px;
`;

const Handle = styled.View`
  width: 40px;
  height: 4px;
  background-color: ${({ theme }) => theme.colors.neutral300};
  border-radius: 2px;
  align-self: center;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

export const Modal: React.FC<ModalProps> = ({ visible, onClose, title, children, ...props }) => {
  const theme = useTheme();

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
      {...props}
    >
      <Overlay>
        <TouchableOpacity
          style={{ flex: 1 }}
          activeOpacity={1}
          onPress={onClose}
          // Prevent clicks on modal content from closing
        />
        <ModalContent
          onStartShouldSetResponder={() => true}
          onTouchEnd={e => {
            // Prevent clicks inside modal from closing it
            e.stopPropagation();
          }}
        >
          <Handle />
          {title && (
            <Header>
              <Text variant="title" color="textPrimary">
                {title}
              </Text>
              <CloseButton onPress={onClose}>
                <Icon name="close" size={24} color={theme.colors.textSecondary} />
              </CloseButton>
            </Header>
          )}
          {children}
        </ModalContent>
      </Overlay>
    </RNModal>
  );
};
